import { useMemo } from 'react';

import { FraudAnalysisStatus, FraudRiskLevel } from '~/core/shared/constants';
import {
  AnalysisResult,
  FileAttribute,
  FraudAnalysisUpdate,
  Nullable,
} from '~/core/shared/types';

/**
 * Projects fraud analysis updates onto a file attribute.
 *
 * NOTE: This is not an ideal pattern. A data store pattern or
 * subscribing to webhooks in this hook would be better. However, components
 * in this tree unmount frequently, losing state. The component tree here needs significant
 * refactoring. This approach (table-level subscription passed down as props) will do for now.
 */
export const useUpToDateFileAttribute = (
  file?: FileAttribute,
  fraudAnalysisUpdates?: FraudAnalysisUpdate[],
): FileAttribute | undefined => {
  // Reduce over fraud updates and project onto the file attribute
  const updatedFile = useMemo(() => {
    if (!file) return undefined;

    // Find the latest fraud update for this attachment (use findLast to get most recent)
    const fraudUpdate = fraudAnalysisUpdates?.findLast(
      (update) => update.attachmentId === file.id,
    );

    // If no update found, return original file
    if (!fraudUpdate) return file;

    // Project the fraud update onto the file's fraudRisk
    return {
      ...file,
      downloadReady: fraudUpdate.downloadReady ?? file.downloadReady,
      redactionStatus: fraudUpdate.redactionStatus ?? file.redactionStatus,
      fraudRisk: {
        ...file.fraudRisk,
        status: fraudUpdate.status as FraudAnalysisStatus,
        displayStatus:
          fraudUpdate.displayStatus ?? file.fraudRisk?.displayStatus,
        riskLevel:
          (fraudUpdate.riskLevel as Nullable<FraudRiskLevel | undefined>) ??
          file.fraudRisk?.riskLevel,
        riskScore: fraudUpdate.riskScore ?? file.fraudRisk?.riskScore,
        analysisResult: fraudUpdate.resultUrl
          ? ({
              ...file.fraudRisk?.analysisResult,
              resultUrl: fraudUpdate.resultUrl,
            } as AnalysisResult)
          : file.fraudRisk?.analysisResult,
      },
    };
  }, [file, fraudAnalysisUpdates]);

  return updatedFile;
};
